import type { JSX } from 'react';

import type { Bereichsgruppe } from './Rahmen';
import { Sinnbild } from './Sinnbild';

/**
 * Die Seite einer Menuegruppe (12.09.2026).
 *
 * ============================================================================
 * WOFÜR SIE DA IST
 * ============================================================================
 *
 * Auftraggeber: „mittlerweile ist das linke menü so voll und führt zum
 * scrollen, dass ich überlege, dass die überschriften die menüpunkte werden und
 * eine seite aufrufen, in der die anderen punkte aufrufbar werden. dort könnten
 * dann auch kurze infos zu den einzelnen punkten stehen."
 *
 * Zwei Aufgaben, und die zweite ist die wertvollere:
 *
 *   1. Sie hielt die Menüspalte kurz — acht Punkte statt einunddreißig. Seit
 *      ÄA-41 steht dieselbe Folge als Menüzeile; die Seite ist die
 *      Übersichtsseite einer Gruppe und seit Auftrag #25 über den Namen der
 *      Gruppe in der Menüzeile erreichbar (ein Verweis, kein Aufklappeintrag).
 *   2. Sie sagt, was hinter einem Punkt liegt. „Belege und Vorlagen" verrät
 *      niemandem, dass dort die Stornosätze stehen; „Besetzung" nicht, dass dort
 *      die Fristen laufen. Bis hierher musste man jeden Punkt einmal anklicken,
 *      um das zu erfahren — und sich merken.
 *
 * ============================================================================
 * SIE ZEIGT NUR, WAS DER ANWENDER DARF
 * ============================================================================
 *
 * Die Karten kommen aus `sichtbareGruppen()` und tragen damit dieselbe
 * Rechtefilterung wie die Spalte (Pflichtenheft 4.3). Eine Karte, die auf eine
 * Abweisung führt, wäre schlimmer als keine: Sie verspricht etwas und sagt es
 * erst beim Anfassen ab.
 *
 * Eine Gruppe mit nur EINEM sichtbaren Punkt bekommt gar keine Seite — der
 * Aufrufer springt dann direkt in die Maske. Die Begründung steht am
 * `Bereichsgruppe`-Typ.
 */
export function Bereichsseite({
  gruppe,
  bereich,
  beiBereich,
}: {
  readonly gruppe: Bereichsgruppe;
  /**
   * Der Punkt, in dem man zuletzt war — er wird hervorgehoben.
   *
   * Wer von einem Punkt auf die Gruppenseite zurückgeht, soll sehen, wo er
   * herkam. Ohne das sind alle Karten gleich, und der Weg zurück ist ein
   * Suchvorgang.
   */
  readonly bereich: string | null;
  readonly beiBereich: (kennung: string) => void;
}): JSX.Element {
  return (
    /*
     * SEITENTYP ÜBERSICHT (ÄA-41): ein Raster aus Karten. Jede trägt die
     * Sinnbildkachel ihres Bereichs, den Namen und den Satz dazu — so, wie der
     * Entwurf seine Aktivitäten zeigt.
     */
    <div className="uebersicht">
      {/* Der Satz zur Gruppe steht als Unterzeile im Seitenkopf — nicht noch einmal hier. */}

      {/*
        EINE LISTE UND KEINE REIHE VON KNÖPFEN.

        Es ist eine Navigation: Ein Vorleseprogramm zählt „4 Einträge" und sagt
        damit, wie weit es noch ist. Zehn lose Knöpfe nebeneinander sagen das
        nicht (WCAG 1.3.1).
      */}
      <nav aria-label={`${gruppe.beschriftung} — Bereiche`}>
        <ul className="bereichsseite">
          {gruppe.bereiche.map((b) => (
            <li key={b.kennung}>
              <button
                type="button"
                className={`bereichsseite__punkt${
                  b.kennung === bereich ? ' bereichsseite__punkt--hier' : ''
                }`}
                aria-current={b.kennung === bereich ? 'page' : undefined}
                onClick={() => {
                  beiBereich(b.kennung);
                }}
              >
                <span className="bereichsseite__kachel" aria-hidden="true">
                  <Sinnbild name={b.sinnbild} />
                </span>
                <span className="bereichsseite__text">
                  <b>{b.beschriftung}</b>
                  <span>{b.erklaerung}</span>
                </span>
              </button>
            </li>
          ))}
        </ul>
      </nav>
    </div>
  );
}
